// src/Pages/VerifyPhone.js
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  linkWithCredential,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

const VerifyPhone = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Data passed from Signup.js (Google signup)
  const passed = location.state || {};
  const [uid, setUid] = useState(passed.uid || null);
  const [name, setName] = useState(passed.name || "");
  const [email, setEmail] = useState(passed.email || "");
  const [phone, setPhone] = useState(passed.phone || "");

  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationId, setVerificationId] = useState(null);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const recaptchaRendered = useRef(false);

  useEffect(() => {
    // fallback if uid not passed but user exists
    const user = auth.currentUser;
    if (!uid && user) {
      setUid(user.uid);
      setName(user.displayName || "");
      setEmail(user.email || "");
      if (user.phoneNumber) setPhone(user.phoneNumber);
    }
  }, [uid]);

  useEffect(() => {
    if (recaptchaRendered.current) return;
    recaptchaRendered.current = true;

    try {
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(
          "recaptcha-container",
          { size: "invisible" },
          auth
        );
      }
    } catch (err) {
      console.warn("recaptcha setup failed:", err);
    }
  }, []);

  // Send OTP
  const sendOtp = async () => {
    setError("");
    setMessage("");

    if (!phone || !phone.startsWith("+") || phone.length < 10) {
      setError("Enter valid phone number with country code. Example: +9779800000000");
      return;
    }

    setLoading(true);
    try {
      // For localhost testing only
      auth.settings.appVerificationDisabledForTesting = true;

      const appVerifier = window.recaptchaVerifier;
      const confirmationResult = await signInWithPhoneNumber(auth, phone, appVerifier);

      setVerificationId(confirmationResult.verificationId);
      setVerificationSent(true);
      setMessage("OTP sent to " + phone);
    } catch (err) {
      console.error("sendOtp error:", err);
      setError(err.message || "Failed to send OTP.");
      // Reset recaptcha
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = new RecaptchaVerifier(
          "recaptcha-container",
          { size: "invisible" },
          auth
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Confirm OTP
  const confirmOtp = async () => {
    setError("");
    setMessage("");

    if (!verificationId || !otp || otp.length < 4) {
      setError("Enter valid OTP.");
      return;
    }

    setLoading(true);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, otp);

      const currentUser = auth.currentUser;
      if (currentUser) {
        // link phone with current user
        await linkWithCredential(currentUser, credential);

        if (uid) {
          await setDoc(
            doc(db, "users", uid),
            { phone: phone, name: name || currentUser.displayName || "", email: email || currentUser.email || "" },
            { merge: true }
          );
        }

        setMessage("Phone verified! Redirecting...");
        setTimeout(() => navigate("/"), 1000);
      } else {
        setMessage("Verified. Redirecting...");
        setTimeout(() => navigate("/"), 800);
      }
    } catch (err) {
      console.error("confirmOtp error:", err);
      setError(err.message || "OTP verification failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Redirect if no user
  useEffect(() => {
    if (!uid && !auth.currentUser) {
      setTimeout(() => navigate("/signup"), 400);
    }
  }, [uid, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-100 to-purple-200 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 space-y-6">
        <h2 className="text-2xl font-bold text-gray-800 text-center">Phone Verification</h2>

        {(name || email) && (
          <div className="text-center">
            <p className="font-semibold">{name || "Welcome"}</p>
            {email && <p className="text-sm text-gray-500">{email}</p>}
            <p className="text-xs text-gray-400 mt-1">Please verify your phone to continue.</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium">Phone number (with country code)</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+9779800000000"
            className="w-full px-4 py-2 mt-1 border rounded-lg"
          />
        </div>

        <div id="recaptcha-container" />

        {!verificationSent ? (
          <button
            onClick={sendOtp}
            disabled={loading}
            className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            {loading ? "Sending..." : "Send OTP"}
          </button>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium">Enter OTP</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="123456"
                className="w-full px-4 py-2 mt-1 border rounded-lg"
              />
            </div>

            <button
              onClick={confirmOtp}
              disabled={loading}
              className="w-full py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>

            <button
              onClick={() => {
                setVerificationSent(false);
                setOtp("");
                setVerificationId(null);
              }}
              className="w-full mt-2 py-2 border rounded-lg text-center"
            >
              Change number
            </button>
          </>
        )}

        {message && <p className="text-green-600 text-sm text-center mt-2">{message}</p>}
        {error && <p className="text-red-600 text-sm text-center mt-2">{error}</p>}
      </div>
    </div>
  );
};

export default VerifyPhone;
